import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { EligibilityModal } from '../components/EligibilityModal';
import { eligibilitiesApi } from '../eligibilitiesApi';
import type { EligibilityListItem } from '../eligibilityTypes';

/**
 * The eligibility catalog — the vocabulary a position's qualification standards may require,
 * and the one an employee's earned eligibilities are filed under.
 *
 * Reached through **Personnel → Settings**. It sat under Organization → Settings while
 * positions were the only thing binding it; CS Form 212 section IV made personnel the second
 * consumer and the heavier one, so the catalog moved to the module that reads it most and the
 * old path redirects.
 *
 * The anchor opens the **profile**, not the edit form: every index page in this product is
 * partnered with one, and here the profile is where the count of titles requiring an
 * eligibility is worth reading before deciding anything about the row.
 */
export function EligibilitiesIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<EligibilityListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    eligibility: EligibilityListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchEligibilities = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      eligibilitiesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (eligibility: EligibilityListItem): MenuItem[] => [
      {
        key: `edit-${eligibility.id}`,
        label: 'Edit',
        permission: 'eligibilities.update',
        onClick: () => setEditing(eligibility),
      },
      {
        key: `restore-${eligibility.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'eligibilities.delete',
        disabledReason: eligibility.isActive ? 'This eligibility is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', eligibility }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await eligibilitiesApi.deactivate(confirming.eligibility.id);
      else await eligibilitiesApi.activate(confirming.eligibility.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<EligibilityListItem>
        module="eligibilities"
        // Reached through Personnel → Settings, so Settings links: that crumb is the way back.
        breadcrumb={[
          'Human Resources',
          'Personnel',
          { label: 'Settings', to: '/personnel/settings' },
          'Eligibilities',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['Eligibility']}
        rowKey={(eligibility) => eligibility.id}
        fetch={fetchEligibilities}
        reloadKey={reloadKey}
        searchPlaceholder="Search eligibilities"
        emptyMessage="No eligibilities yet. Add the ones this organization recognises."
        onCreate={() => setCreating(true)}
        createPermission="eligibilities.create"
        // The anchor opens the profile; Edit is in the row menu. The read key, not the write
        // one — opening a record is not changing it.
        onRowOpen={(eligibility) => navigate(`/personnel/settings/eligibilities/${eligibility.id}`)}
        openPermission="eligibilities.read"
        rowActions={rowActions}
        onDelete={(eligibility) => setConfirming({ kind: 'deactivate', eligibility })}
        deletePermission="eligibilities.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(eligibility) =>
          eligibility.isActive ? null : 'This eligibility is already deleted.'
        }
        onClearData={() => eligibilitiesApi.clear()}
        clearDataPermission="eligibilities.clear-data"
        otherInfo={(eligibility) => eligibility}
      />

      {creating || editing !== null ? (
        <EligibilityModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete eligibility' : 'Restore eligibility'}
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring needs no ceremony.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`eligibility-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.eligibility.name}" stops being offered on a position's qualification standards.`
                  : `"${confirming.eligibility.name}" is offered again on a position's qualification standards.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.eligibility.positionCount > 0 ? (
                <p>
                  {confirming.eligibility.positionCount} position
                  {confirming.eligibility.positionCount === 1 ? '' : 's'} still require
                  {confirming.eligibility.positionCount === 1 ? 's' : ''} it. They keep resolving
                  its name — deleting a word does not rewrite what was recorded — but it cannot
                  be chosen for anything new.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted eligibility can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<EligibilityListItem>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 300,
    flex: true,
    sortable: true,
    render: (eligibility) => <TextCell value={eligibility.name} />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 320,
    summary: true,
    render: (eligibility) => <TextCell value={eligibility.description} />,
  },
  {
    key: 'positionCount',
    header: 'Positions',
    width: 110,
    align: 'right',
    sortable: true,
    summary: true,
    // Counted from the position catalog, which is another module's records — the name and
    // description beside it are this module's own and stay ungated.
    permission: 'positions.read',
    render: (eligibility) => <TextCell value={String(eligibility.positionCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (eligibility) =>
      eligibility.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
